import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useUser } from '@/shared/hooks/useAuth';
import { grantedSections, isSectionVisible } from '@/shared/permissions/moduleSections';
import { effectiveWorkspaceAccess } from '@/shared/permissions/workspaceAccess';
import { ROUTES, navGroupsForRole, navPathAllowed } from './paths';

/**
 * Section gate for the authenticated shell.
 *
 * `moduleSections.ts` already computed, tested and documented per-screen access
 * — but `isSectionVisible` was called from nowhere in the app, so a user whose
 * section grants hid a screen from the sidebar could still reach it by typing
 * the URL. Hiding a link is not a gate. This mounts the existing rule once, as
 * a layout route wrapping every screen inside AppShell, so a new page is
 * covered the moment it is added to NAV_GROUPS rather than needing its own
 * guard.
 *
 * Ordering relative to WorkspaceRoute is deliberate and matches the documented
 * model: MODULE first (WorkspaceRoute, per route), SECTION second (here). A
 * section grant can only narrow a module the user already holds — it can never
 * widen one — so running the narrower gate outermost is safe.
 *
 * Presentation only, exactly like WorkspaceRoute. The server's module gate in
 * `requireAuth` (workspaceModuleForRequest → hasWorkspaceAccess) and each
 * route's own permission guard remain the authorization boundary; this stops a
 * user wandering into a screen that will only 403 at them.
 */
export function SectionRoute() {
  const user = useUser();
  const location = useLocation();
  const sections = grantedSections(user);

  const workspaceAccess = effectiveWorkspaceAccess(user);
  const navAllowed = navPathAllowed(location.pathname, user?.role, user?.department, workspaceAccess, user?.actionPermissions, user);
  if (navAllowed && isSectionVisible(sections, location.pathname)) return <Outlet />;

  // A sidebar screen this user's sidebar does not offer: send them to the
  // first screen it does offer (the Dashboard itself is hidden for Accounts,
  // so a fixed fallback could loop).
  if (!navAllowed) {
    const home = navGroupsForRole(user?.role, user?.department, workspaceAccess, sections, user?.actionPermissions, user)[0]?.items[0]?.path;
    if (home && home !== location.pathname) return <Navigate to={home} replace />;
  }

  // Redirecting to a dashboard the user also cannot open would loop forever, so
  // the fallback destination is checked before it is used. An administrator who
  // grants no sections at all gets a plain explanation rather than a hung tab.
  if (!isSectionVisible(sections, ROUTES.dashboard)) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-2 text-center">
        <h1 className="text-headline-sm font-semibold text-on-surface">No screens assigned</h1>
        <p className="max-w-md text-body-sm text-on-surface-variant">
          Your account has no sections enabled yet. Ask an administrator to grant access
          under Roles &amp; Permissions.
        </p>
      </div>
    );
  }

  return <Navigate to={ROUTES.dashboard} replace />;
}
